/**
 * F21 Hifdh tracker — local-only progress (localStorage). No PII.
 * Keys: "surahNumber:verseNumber" → { status, lastReviewed }
 */
const STORAGE_KEY = 'hifdh_progress_v1';
const REVIEW_CAP = 20;

/** @typedef {'none'|'learning'|'memorized'} HifdhStatus */

function loadMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveMap(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
}

function keyOf(surahNumber, verseNumber) {
  return `${surahNumber}:${verseNumber}`;
}

/**
 * @returns {HifdhStatus}
 */
export function getHifdhStatus(surahNumber, verseNumber) {
  const map = loadMap();
  const k = keyOf(surahNumber, verseNumber);
  const row = map[k];
  if (!row || !row.status) return 'none';
  if (row.status === 'learning' || row.status === 'memorized') return row.status;
  return 'none';
}

/**
 * @param {HifdhStatus|'none'} status
 */
export function setHifdhStatus(surahNumber, verseNumber, status) {
  const map = loadMap();
  const k = keyOf(surahNumber, verseNumber);
  if (status === 'none') {
    delete map[k];
  } else {
    const prev = map[k] || {};
    map[k] = {
      status,
      lastReviewed:
        status === 'memorized' ? new Date().toISOString() : prev.lastReviewed || null,
    };
  }
  saveMap(map);
}

/** Update last reviewed time for a memorized ayah (user repeated / reviewed). */
export function touchHifdhReview(surahNumber, verseNumber) {
  const map = loadMap();
  const k = keyOf(surahNumber, verseNumber);
  const row = map[k];
  if (!row || row.status !== 'memorized') return;
  row.lastReviewed = new Date().toISOString();
  map[k] = row;
  saveMap(map);
}

/**
 * Review queue: memorized items with oldest lastReviewed first (nulls first).
 * @returns {{ surahNumber: number, verseNumber: number, lastReviewed: string|null }[]}
 */
export function getHifdhReviewQueue(max = REVIEW_CAP) {
  const map = loadMap();
  const rows = [];
  Object.entries(map).forEach(([k, v]) => {
    if (!v || v.status !== 'memorized') return;
    const [sn, vn] = k.split(':').map(Number);
    if (!sn || !vn) return;
    rows.push({
      surahNumber: sn,
      verseNumber: vn,
      lastReviewed: v.lastReviewed || null,
    });
  });
  rows.sort((a, b) => {
    if (!a.lastReviewed && !b.lastReviewed) return 0;
    if (!a.lastReviewed) return -1;
    if (!b.lastReviewed) return 1;
    return new Date(a.lastReviewed).getTime() - new Date(b.lastReviewed).getTime();
  });
  return rows.slice(0, max);
}

/** @returns {{ learning: number, memorized: number }} */
export function getHifdhCounts() {
  const map = loadMap();
  let learning = 0;
  let memorized = 0;
  Object.values(map).forEach((v) => {
    if (!v || !v.status) return;
    if (v.status === 'learning') learning += 1;
    if (v.status === 'memorized') memorized += 1;
  });
  return { learning, memorized };
}

/**
 * Verses marked "learning" (no ordering guarantee).
 * @returns {{ surahNumber: number, verseNumber: number }[]}
 */
export function getHifdhLearningVerses(max = 40) {
  const map = loadMap();
  const rows = [];
  Object.entries(map).forEach(([k, v]) => {
    if (!v || v.status !== 'learning') return;
    const [sn, vn] = k.split(':').map(Number);
    if (!sn || !vn) return;
    rows.push({ surahNumber: sn, verseNumber: vn });
  });
  return rows.slice(0, max);
}

/** Remove per-person hifdh progress from this device (called on logout / session expiry). */
export function clearHifdhProgress() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
